import React, { useEffect, useState, useCallback } from 'react';
import { RefreshCw, Loader2, X, AlertCircle } from 'lucide-react';
import { api } from '../../lib/api';
import { Card, LoadingState, ErrorState } from '../../components/control-center/StateWrappers';
import { Button } from '../../components/ui/button';
import { useCC, StatusBadge } from '../../components/control-center/Layout';

const Settings = () => {
  const { role, refreshMe, refreshOverview } = useCC();
  const isAdmin = role === 'admin';

  const [state, setState] = useState({ loading: true, error: false, active: null });
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(false); // when toggling ON→OFF
  const [errMsg, setErrMsg] = useState('');

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: false }));
    setErrMsg('');
    try {
      const { data } = await api.get('/control-center/overview');
      setState({ loading: false, error: false, active: !!data?.hotel?.automation_active });
    } catch { setState({ loading: false, error: true, active: null }); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const applyValue = async (value) => {
    setSaving(true);
    setErrMsg('');
    try {
      await api.patch('/control-center/automation', { value });
      await load();
      await refreshMe?.();
      await refreshOverview?.();
      setConfirm(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setErrMsg(typeof detail === 'string' ? detail : 'No hemos podido cambiar el estado. Inténtalo de nuevo.');
    } finally {
      setSaving(false);
    }
  };

  const onToggle = () => {
    if (!isAdmin || saving) return;
    if (state.active) setConfirm(true);        // ON → OFF requires confirmation
    else applyValue(true);                     // OFF → ON is direct
  };

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-start sm:items-center justify-between flex-col sm:flex-row gap-3 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Ajustes</h1>
          <p className="text-slate-500 text-sm mt-1">Configuración de la automatización de correo.</p>
        </div>
        <Button onClick={load} className="bg-white text-slate-800 border border-slate-200 hover:bg-slate-50 gap-2">
          <RefreshCw className={`w-4 h-4 ${state.loading ? 'animate-spin' : ''}`} /> Actualizar
        </Button>
      </div>

      {state.loading && <LoadingState />}
      {state.error && !state.loading && <ErrorState onRetry={load} />}
      {!state.loading && !state.error && (
        <Card className="p-6 md:p-8">
          <div className="flex items-start justify-between gap-4 mb-2">
            <div>
              <p className="text-slate-900 font-semibold">Automatización de correo Aivanilo</p>
              <p className="text-slate-500 text-sm mt-1">
                {state.active
                  ? 'Los nuevos correos se procesan automáticamente.'
                  : 'La automatización está pausada. No se procesan correos entrantes.'}
              </p>
              <div className="mt-3"><StatusBadge active={state.active} /></div>
            </div>
            {/* Toggle */}
            <button
              onClick={onToggle}
              disabled={!isAdmin || saving}
              role="switch"
              aria-checked={!!state.active}
              data-testid="automation-toggle"
              className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${state.active ? 'bg-green-500' : 'bg-slate-300'} ${(!isAdmin || saving) ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${state.active ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>

          {!isAdmin && (
            <p className="mt-4 text-xs text-slate-500 flex items-center gap-1.5">
              <AlertCircle className="w-3.5 h-3.5" /> Solo los administradores pueden cambiar este estado.
            </p>
          )}
          {errMsg && (
            <p className="mt-4 text-sm text-red-600 flex items-center gap-1.5" data-testid="automation-error"><AlertCircle className="w-4 h-4" /> {errMsg}</p>
          )}
        </Card>
      )}

      {/* Confirmation modal */}
      {confirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-slate-900/50" onClick={() => !saving && setConfirm(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6" data-testid="automation-confirm">
            <div className="flex items-start justify-between mb-3">
              <h3 className="text-lg font-bold text-slate-900">¿Pausar la automatización de Aivanilo?</h3>
              <button onClick={() => !saving && setConfirm(false)} className="p-1 rounded hover:bg-slate-100" aria-label="Cerrar"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-slate-600 text-sm mb-6">Los nuevos correos no se procesarán hasta que vuelvas a activarla.</p>
            <div className="flex justify-end gap-2">
              <Button onClick={() => !saving && setConfirm(false)} disabled={saving} className="bg-white text-slate-800 border border-slate-200 hover:bg-slate-50">Cancelar</Button>
              <Button onClick={() => applyValue(false)} disabled={saving} data-testid="automation-confirm-pause" className="bg-slate-900 hover:bg-slate-800 text-white gap-2">
                {saving && <Loader2 className="w-4 h-4 animate-spin" />} Pausar automatización
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
